import React, { useRef } from 'react';
import { Controller } from 'react-hook-form';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import FileDropzone from 'components/FileDropzone/FileDropzone';
import { inferMimeTypeFromName } from 'modules/FileManagerV2/utils/file/mimeFallback';
import { isAllowedFile, FILE_MANAGER_ACCEPT } from 'modules/FileManagerV2/constants/fileManagerConstants';

function sanitizeFileName(name) {
  return name
    .normalize('NFC')
    .replace(/[^\x20-\x7E]/g, '_')
    .replace(/_{2,}/g, '_')
    .trim();
}

function makeUniqueNames(files, existingFiles = []) {
  const nameCountMap = new Map();
  existingFiles.forEach((file) => {
    nameCountMap.set(file.name, (nameCountMap.get(file.name) || 0) + 1);
  });
  return files?.map((file) => {
    let name = sanitizeFileName(file.name);
    const [base, ext] = (() => {
      const idx = name.lastIndexOf('.');
      return idx !== -1 ? [name.slice(0, idx), name.slice(idx)] : [name, ''];
    })();

    while (nameCountMap.has(name)) {
      const count = nameCountMap.get(name);
      name = `${base}(${count})${ext}`;
      nameCountMap.set(file.name, count + 1);
    }
    nameCountMap.set(name, 1);
    const fallbackType = file.type || inferMimeTypeFromName(name);
    const newFile = new File([file], name, { type: fallbackType });
    if (file.webkitRelativePath) {
      Object.defineProperty(newFile, 'webkitRelativePath', {
        value: file.webkitRelativePath.replace(file.name, name),
        writable: false,
        configurable: true,
      });
    } else if (file.path) {
      // react-dropzone sets `path` (not webkitRelativePath) when a folder is dragged in
      const segments = String(file.path).replace(/^\/+/, '').split('/').filter(Boolean);
      if (segments.length) {
        segments[segments.length - 1] = name;
        Object.defineProperty(newFile, 'webkitRelativePath', {
          value: segments.join('/'),
          writable: false,
          configurable: true,
        });
      }
    }
    return newFile;
  });
}

/**
 * Recursively reads all files from a FileSystemDirectoryHandle (File System Access API).
 * Sets webkitRelativePath on each file to mirror the path format produced by webkitdirectory inputs.
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {string} path - accumulated relative path (starts with the root folder name)
 * @returns {Promise<File[]>}
 */
async function readDirHandle(dirHandle, path = '') {
  const files = [];
  for await (const [name, entry] of dirHandle) {
    const entryPath = path ? `${path}/${name}` : name;
    if (entry.kind === 'file') {
      const file = await entry.getFile();
      Object.defineProperty(file, 'webkitRelativePath', {
        value: entryPath,
        writable: false,
        configurable: true,
      });
      files.push(file);
    } else if (entry.kind === 'directory') {
      const subFiles = await readDirHandle(entry, entryPath);
      files.push(...subFiles);
    }
  }
  return files;
}

const browseButtonStyle = {
  border: '1px solid var(--gray-light)',
  borderRadius: '6px',
  background: 'transparent',
  padding: '6px 18px',
  fontSize: '13px',
  color: 'var(--black-600)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'background 0.15s',
};

function UploadInput({ control, errors }) {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const onDropTransform = (acceptedFiles, currentFiles) => {
    const filtered = acceptedFiles.filter(isAllowedFile);
    return makeUniqueNames(filtered, currentFiles);
  };

  return (
    <div className="dropzone-wraper d-flex align-items-center py-2 flex-wrap">
      <div className="col-md-3 col-12">
        <label className="p-medium">
          Source <span style={{ color: 'red' }}>*</span>
        </label>
      </div>
      <div className="col-md-9 col-12">
        <Controller
          name="files"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value = [] } }) => {
            const handleFileSelect = (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const transformed = onDropTransform(files, value);
              onChange([...value, ...(transformed || [])]);
              e.target.value = '';
            };

            const handleFolderSelect = (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const transformed = onDropTransform(files, value);
              onChange([...value, ...(transformed || [])]);
              e.target.value = '';
            };

            const handleFolderButtonClick = async (e) => {
              e.stopPropagation();
              if (typeof window.showDirectoryPicker === 'function') {
                try {
                  const dirHandle = await window.showDirectoryPicker();
                  const files = await readDirHandle(dirHandle, dirHandle.name);
                  if (files.length) {
                    const transformed = onDropTransform(files, value);
                    onChange([...value, ...(transformed || [])]);
                  }
                } catch (err) {
                  if (err?.name === 'AbortError') return;
                  // API available but failed unexpectedly — fall back
                  folderInputRef.current?.click();
                }
              } else {
                folderInputRef.current?.click();
              }
            };

            const browseActions = (
              <div className="d-flex gap-2 upload-browse-actions align-items-center">
                <button
                  type="button"
                  style={browseButtonStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <i className="pi pi-file" style={{ fontSize: '11px' }} />
                  Choose files
                </button>
                <span className="F-size12 mb-2" style={{ color: 'var(--black-400)' }}>
                  or
                </span>
                <button type="button" style={browseButtonStyle} onClick={handleFolderButtonClick}>
                  <i className="pi pi-folder" style={{ fontSize: '11px' }} />
                  Choose folder
                </button>
              </div>
            );

            return (
              <>
                <FileDropzone
                  value={value}
                  onChange={onChange}
                  accept={FILE_MANAGER_ACCEPT}
                  // maxFiles={20}
                  allowDirectory
                  noDragEventsBubbling
                  onDropTransform={onDropTransform}
                  renderActions={browseActions}
                />

                {/* Hidden input — regular file picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={FILE_MANAGER_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {/* Hidden input — folder picker */}
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  mozdirectory=""
                  style={{ display: 'none' }}
                  onChange={handleFolderSelect}
                />
              </>
            );
          }}
        />
        {errors?.files && <InputErrorMessage>Required</InputErrorMessage>}
      </div>
    </div>
  );
}

export default UploadInput;
